import { PartyPopper } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { Button } from '@/shared/ui/shadcn/button';

import type { LabOrderSubmissionState } from '../../model/submission/orchestrator.js';

export interface ConfirmationStepProps {
  submissionState: LabOrderSubmissionState;
  onRetryCreateTest: () => Promise<void>;
  onRetrySubmitOrder: () => Promise<void>;
}

export function ConfirmationStep({
  submissionState,
  onRetryCreateTest,
  onRetrySubmitOrder,
}: ConfirmationStepProps) {
  const { status, remoteOrderId, labTestId, errorMessage } = submissionState;

  return (
    <div className="flex flex-col gap-4">
      {status === 'success' ? (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2 text-sm font-medium">
            <PartyPopper className="size-4" aria-hidden />
            Lab order submitted
          </div>
          <div className="text-sm text-muted-foreground">
            The lab order has been successfully submitted. The patient will be notified with next
            steps.
          </div>

          {remoteOrderId !== null ? (
            <div className="mt-3 text-sm text-muted-foreground">
              Order ID: <span className="font-mono text-foreground">{remoteOrderId}</span>
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 text-sm font-medium">Confirmation</div>
            <div className="text-sm text-muted-foreground">
              {status === 'failed-missing-patient-info'
                ? 'Order could not be submitted due to incomplete patient information.'
                : status === 'failed-submit-order'
                  ? 'Lab test created but order could not be submitted.'
                  : status === 'failed-create-test'
                    ? 'Lab test could not be created.'
                    : 'Review the outcome below.'}
            </div>
          </div>

          {labTestId !== null ? (
            <div className="text-sm text-muted-foreground">
              Lab Test ID: <span className="font-mono text-foreground">{labTestId}</span>
            </div>
          ) : null}
        </>
      )}

      {status === 'failed-missing-patient-info' ? (
        <Alert variant="destructive">
          <AlertTitle>Incomplete patient information</AlertTitle>
          <AlertDescription>
            {errorMessage ??
              'Required patient demographics are missing. Please complete the patient profile before ordering lab tests.'}
          </AlertDescription>
        </Alert>
      ) : null}

      {status === 'failed-submit-order' ? (
        <Alert variant="destructive">
          <AlertTitle>Test created, order not submitted</AlertTitle>
          <AlertDescription>
            <div>
              {errorMessage ?? 'The lab test was created but the order could not be submitted.'}
            </div>
            <div className="pt-3">
              <Button
                type="button"
                radius="pill"
                onClick={() => void onRetrySubmitOrder()}
                disabled={labTestId === null}
              >
                Retry submit
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      ) : null}

      {status === 'failed-create-test' ? (
        <Alert variant="destructive">
          <AlertTitle>Create test failed</AlertTitle>
          <AlertDescription>
            <div>{errorMessage ?? 'The lab test could not be created.'}</div>
            <div className="pt-3">
              <Button type="button" radius="pill" onClick={() => void onRetryCreateTest()}>
                Retry create
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
}
